import { defineRoutes } from '@makan/contracts/client';
import type { z } from 'zod';
import type { CrmSettings, PointsRules } from './commands.ts';

/**
 * crm's values entry: the facts code outside the domain reads by value (the
 * join page, crm's checks), defined once. Bundle-safe: it and the values
 * modules it re-exports (*.values.ts) import only libphonenumber-js/min,
 * @makan/contracts/client and types (rule R20), so the public page pays
 * nothing for zod, and a page that does not parse a phone does not load the
 * parser's metadata. commands.ts builds its schemas on these, so the page
 * and the decider cannot disagree.
 */

/** crm's pages. The command center binds a page to each (features/crm/ui); the feature map and the dev banner read them. */
export const routes = defineRoutes({
  join: { path: '/join/:groupId', access: 'public', hostHome: true, intent: 'The poster QR lands here: phone, code, consent per marketing channel, the opening voucher.' },
  members: { path: '/crm/members', access: 'staff', staffHome: true, intent: 'Staff list of members: masked phones, source, consent per channel, visits and outlets; the sign-up link for a source tag.' },
  redeem: { path: '/crm/redeem', access: 'staff', intent: 'The counter: pick the outlet once, scan the member QR, pick one of their vouchers, redeem it.' },
  campaigns: { path: '/crm/campaigns', access: 'staff', intent: 'The campaigns with their results; a new event QR: an offer and a campaign giving it by link, its QR to print.' },
  vouchers: { path: '/crm/vouchers', access: 'staff', intent: 'The voucher catalogue with what each gave and how much was used; a new voucher, into a campaign or with a QR of its own.' },
  voucherStation: { path: '/v/:groupId', access: 'public', intent: "A voucher station: one button that shows a customer a single-use QR for a draw of its campaign; a manager sets the device up here once." },
  privacy: { path: '/privacy/:groupId', access: 'public', intent: "The full PDPA privacy notice the join page sums up: what the group keeps, why, who it shares it with, how long, and how to see, correct or delete it." },
  member: { path: '/m/:groupId', access: 'public', intent: "A member's own page: the member QR to show at the counter, every voucher with its terms, the invite link." },
});

/** The most tiers a group sets. */
export const MAX_TIERS = 10;

/**
 * The points rules a group holds until it sets its own (`set points rules`):
 * Kampong Mala's at launch, 1 point per RM 1 spent, each worth 1 sen of
 * cashback, and Bronze from 0, Silver from 2,000 and Gold from 5,000 points
 * earned (Joel, 2026-10-29).
 */
export const DEFAULT_POINTS_RULES = {
  pointsPerUnit: 1,
  senPerPoint: 1,
  tiers: [
    { name: 'Bronze', from: 0 },
    { name: 'Silver', from: 2000 },
    { name: 'Gold', from: 5000 },
  ],
} as const satisfies z.input<typeof PointsRules>;

/** The first member's number; each member who joins after gets the next, so a number has six digits until the 900,000th member. */
export const FIRST_MEMBER_NUMBER = 100_001;

/** crm's media slots (the manifest's `media`): an offer's picture, shown on its vouchers to anyone (`public`). */
export const MEDIA_SLOTS = {
  'offer-image': { types: ['image/png', 'image/jpeg'], maxBytes: 2 * 1024 * 1024, public: true },
} as const;

/** What a group answers before its first `change crm settings` (the decider parses it with CrmSettings at load). */
export const DEFAULT_CRM_SETTINGS = {
  channel: 'sms',
  codeTtlMinutes: 10,
  maxAttempts: 5,
  maxCodesPerDay: 2_000,
  resendAfterSeconds: 30,
  maxCodesPerWindow: 5,
  codeWindowMinutes: 10,
  noticeVersion: 'ngj-draft-2026-10',
  voucherConditions: [],
  brandConditions: [],
  points: DEFAULT_POINTS_RULES,
} as const satisfies z.input<typeof CrmSettings>;

/** A voucher's terms and conditions: at most this many lines a person adds, each at most this long. */
export const VOUCHER_TERMS = { lines: 20, length: 300 } as const;

/**
 * The default terms a voucher of these brands starts with: the brands' own,
 * in order and once each, else the group's; none when neither has any. The
 * decider gives them to an offer published without terms of its own, and the
 * voucher forms pre-fill them, so both read the same rule.
 */
export function defaultTermsOf<L extends string>(settings: { readonly voucherConditions: readonly L[]; readonly brandConditions: readonly { readonly brandId: string; readonly conditions: readonly L[] }[] }, brandIds: readonly string[]): readonly L[] {
  const own = brandIds.flatMap((brandId) => settings.brandConditions.find((brand) => brand.brandId === brandId)?.conditions ?? []);
  return own.length > 0 ? [...new Set(own)] : settings.voucherConditions;
}

export const INVITE_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{8}$/;

export const MEMBER_PASS_PATTERN = /^[A-Za-z0-9_-]{32,64}$/;

/** A printed card's code, as its QR carries it (`?c=`): ten Crockford base32 characters. */
export const CARD_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{10}$/;

/** The secret a manager's browser or the CLI mints for a card print run: the codes derive from it and their serials. */
export const PRINT_SECRET_PATTERN = /^[A-Za-z0-9_-]{32,64}$/;

/** A record's id as a link carries it, a draw's (`?d=`) or a print run's (`?b=`): a UUIDv7. */
export const RECORD_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** How a member says their gender at sign-up (Joel, 2026-10-19; female or male only, 2026-10-22): a choice, never free text. Personal: it lives in the member record. */
export const GENDERS = ['female', 'male'] as const;

/** How a member says their race at sign-up (Kampong Mala launch, Joel 2026-10-29): a choice of four, never free text. Personal: it lives in the member record, never an event or a projection. */
export const RACES = ['malay', 'chinese', 'indian', 'others'] as const;

/** The years a year of birth may be: the page offers the last hundred. */
export const YEAR_OF_BIRTH_BOUNDS = { min: 1900, max: 2100 } as const;

/** The most members one `reveal member details` names: a page of the members list at its largest, so the page and the export reveal a page at a time. */
export const MAX_REVEAL = 500;

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export const IDENTIFICATION_FRESH_MINUTES = 15;

/** A source tag from the sign-up link or QR: a campaign, a poster (ruling 4). */
/** The most cards one print run makes. */
export const MAX_PRINT_RUN_CARDS = 5000;

export const SOURCE_TAG_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

/**
 * How a link the system made reached the person (`utm_medium`): a QR code
 * scanned, a link opened, a printed card, a voucher station. Every link and
 * QR the command center makes for customers carries it with `utm_source` and
 * `utm_campaign`, beside the `?s=` source tag.
 */
export const UTM_MEDIUMS = ['qr', 'link', 'print', 'station'] as const;

/** A `utm_source` or `utm_campaign` value the page passes on: a slug, as a source tag is. A URL's other values are dropped, never recorded. */
export const UTM_VALUE_PATTERN = /^[a-z0-9][a-z0-9_-]{0,59}$/;

/** The most a voucher of a random-one campaign may weigh: its chance is its weight over the sum of the campaign's weights. */
export const MAX_VOUCHER_WEIGHT = 1000;

/** The most vouchers of one offer a campaign may be set to issue. */
export const MAX_OFFER_QUANTITY = 1_000_000;

export { countryOfPhone, dialCountries, parseInternationalPhone, parsePhoneInput, type ParsedPhone } from './phone.values.ts';
export { REDEEM_MINUTE_BOUNDS, REDEEMED_LATER, medianRedeemBound, redeemBucketOf } from './results.values.ts';
export { memberNumberOf, standingOf, type PointsRulesView, type Standing } from './points.values.ts';
